<div [ngClass]="containerClasses()">
  <!-- Sidebar -->
  <aside
    class="so-layout__sidebar"
    [class.so-layout__sidebar--resizable]="resizable() && !isCollapsed()"
    [class.so-layout__sidebar--resizing]="resizable() && !isCollapsed() && isResizing()"
    [ngStyle]="sidebarStyles()">
    <!-- Sidebar Header -->
    <div class="so-layout__sidebar-header">
      @if (sidebarHeader) {
        <ng-container *ngTemplateOutlet="sidebarHeader"></ng-container>
      } @else {
        <div class="so-layout__user-menu-wrapper">
          <button 
            type="button"
            class="so-layout__user-btn"
            [class.so-layout__user-btn--open]="isUserMenuOpen()"
            (click)="toggleUserMenu()">
            @if (logoIcon() || logoText() || userImage()) {
              <span class="so-layout__logo-icon">
                @if (userImage()) {
                  <img [src]="userImage()" alt="User" class="so-layout__user-img" />
                } @else if (logoIcon()) {
                  <so-premium-icon-ui
                    [icon]="logoIcon()"
                    color="--so-secondary"
                    [size]="20"
                    [padding]="8"
                    [borderRadius]="10"
                  ></so-premium-icon-ui>
                } @else if (logoText()) {
                  <span class="so-layout__logo-initials">{{ logoText() }}</span>
                }
              </span>
            }
            @if (!isCollapsed()) {
              <div class="so-layout__logo-text">
                @if (title()) {
                  <span class="so-layout__title">{{ title() }}</span>
                }
                @if (subtitle()) {
                  <span class="so-layout__subtitle">{{ subtitle() }}</span>
                }
              </div>
              <span class="so-layout__user-chevron material-icons">
                {{ isUserMenuOpen() ? 'expand_less' : 'expand_more' }}
              </span>
            }
          </button>

          <!-- User Menu Dropdown -->
          @if (isUserMenuOpen() && !isCollapsed()) {
            <div class="so-layout__user-dropdown">
              @for (action of userMenuActions(); track action.id) {
                <button 
                  type="button"
                  class="so-layout__user-dropdown-item"
                  (click)="onUserMenuAction(action)">
                  @if (action.icon) {
                    <so-svg-icon-ui 
                      [name]="action.icon"
                      [color]="action.color ?? '--so-slate-600'"
                      [size]="18"
                    ></so-svg-icon-ui>
                  }
                  <span>{{ action.label }}</span>
                </button>
              }
              @if (showLogoutButton()) {
                <button 
                  type="button"
                  class="so-layout__user-dropdown-item so-layout__user-dropdown-item--logout"
                  (click)="onLogout()">
                  <so-svg-icon-ui 
                    name="logout"
                    color="--so-danger"
                    [size]="18"
                  ></so-svg-icon-ui>
                  <span>{{ logoutLabel() }}</span>
                </button>
              }
            </div>
          }
        </div>
      }
      
      @if (collapsible()) {
        <button 
          type="button"
          class="so-layout__collapse-btn" 
          (click)="toggleSidebar()">
          <span class="material-icons">
            {{ isCollapsed() ? 'chevron_right' : 'chevron_left' }}
          </span>
        </button>
      }
    </div>

    <!-- Navigation -->
    <nav class="so-layout__nav">
      @for (item of menuItems(); track trackByMenuItem($index, item)) {
        <a 
          class="so-layout__nav-item"
          [class.so-layout__nav-item--active]="isActiveRoute(item.route)"
          [class.so-layout__nav-item--disabled]="item.disabled"
          [routerLink]="item.route"
          [queryParams]="queryParams()"
          routerLinkActive="so-layout__nav-item--active"
          [title]="isCollapsed() ? item.label : ''"
          (click)="onMenuItemClick(item)">
          @if (item.icon) {
            <span 
              class="so-layout__nav-icon"
              [class.so-layout__nav-icon--premium]="isActiveRoute(item.route)">
              @if(isActiveRoute(item.route)){
                <so-premium-icon-ui 
                  [icon]="item.icon"
                  [color]="'--so-secondary'"
                  [size]="16"
                ></so-premium-icon-ui>
              } @else {
                <so-svg-icon-ui 
                  [name]="item.icon"
                  [color]="item.color ?? '--so-gray-thick'"
                  [size]="18"
                ></so-svg-icon-ui>
              }
            </span>
          }
          @if (!isCollapsed()) {
            <span class="so-layout__nav-label">{{ item.label }}</span>
          }
          @if (item.badge && !isCollapsed()) {
            <span class="so-layout__nav-badge">{{ item.badge }}</span>
          }
          @if (isUploadingItem(item)) {
            <span
              class="so-layout__nav-uploading"
              [class.so-layout__nav-uploading--compact]="isCollapsed()"
              [attr.title]="uploadingLabel()"
            >
              <span class="so-layout__nav-uploading-icon">
                <so-svg-icon-ui 
                  name="cloud_upload"
                  [size]="isCollapsed() ? 16 : 18"
                  color="--so-secondary"
                ></so-svg-icon-ui>
              </span>
              <span class="so-layout__nav-uploading-dot"></span>
            </span>
          }
        </a>
      }
    </nav>

    @if (resizable() && !isCollapsed()) {
      <div 
        class="so-layout__resize-handle"
        [class.so-layout__resize-handle--active]="isResizing()"
        (mousedown)="onResizeStart($event)">
      </div>
    }

    <!-- Sidebar Footer -->
    @if(showFooter()){
      <div class="so-layout__sidebar-footer">
        @if (sidebarFooter) {
          <ng-container *ngTemplateOutlet="sidebarFooter"></ng-container>
        } @else if (!isCollapsed()) {
          @if (version()) {
            <span class="so-layout__version">{{ version() }}</span>
          }
          @if (logoText()) {
            <span class="so-layout__app-name">{{ logoText() }}</span>
          }
        }
      </div>
    }
  </aside>

  <!-- Main Content -->
  <main class="so-layout__main" [ngStyle]="mainStyles()">
    <!-- Header -->
    @if (showHeader()) {
      <header class="so-layout__header" [ngStyle]="headerStyles()">
        @if (headerContent) {
          <ng-container *ngTemplateOutlet="headerContent"></ng-container>
        } @else {
          <div class="so-layout__header-spacer"></div>
        }
        
        @if (showThemeToggle()) {
          <button 
            type="button"
            class="so-layout__theme-toggle" 
            (click)="toggleTheme()">
            <span class="material-icons">dark_mode</span>
          </button>
        }
      </header>
    }

    <!-- Content Area -->
    <div class="so-layout__content" [ngStyle]="contentStyles()">
      <ng-content></ng-content>
    </div>
  </main>
</div>
